import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { TableOptions } from "../../../../shared-kernel/components/Table";
import {
    createAdapterToFieldFundsEndpoint,
    createAdapterEndpointToFunds,
    createAdapterToFundsEndpoint,
} from "../adapters/funds.adapter";
import {
    EndpointFundsFiltered,
    FCIMovementItem,
    FundsEndpoint,
    EndpointFunds,
    FCIMovementItemEndpoint,
    EndpointFundsList
} from "../domain/Funds.model";
import { appConfig } from "../../../../config";
import { createAdapterToFilterBackend } from "../../../../utilities/paramsAdapter";
import { createAdapterFromFCIMovementToEndpoint } from "../adapters/fundsImport.adapter";

const BASE_FUNDS_URL = "/entidadesfinancieras/fondos";
const BASE_FUNDS_MUTATION_URL = "/entidadesfinancieras/fondos";
export const BASE_FUNDS_EXPORT_URL = `${appConfig.API_NODE_URL}/entidadesfinancieras/fondos/export`;

export const fundsApi = createApi({ 
    reducerPath: "fundsApi",
    baseQuery: fetchBaseQuery({
        baseUrl: appConfig.API_NODE_URL,
        prepareHeaders: (headers: Headers) => {
            const APP_TOKEN = "app_token";
            const token = localStorage.getItem(APP_TOKEN);

            if (token) {
                headers.set("authorization", `Bearer ${token}`);
            }

            return headers;
        },
        credentials: "include",
    }),
    tagTypes: ["Funds", "Funds"],
    endpoints: (builder) => ({
        getFund: builder.query<FCIMovementItem, number | undefined>({
            query: (id) => `${BASE_FUNDS_MUTATION_URL}/${id}`,
            transformResponse: (response: FCIMovementItemEndpoint) => {
                return createAdapterEndpointToFunds(response);
            },
            providesTags: (_res, _err, id) => [{ type: "Funds", id: id?.toString() }],
        }),
        getFunds: builder.query<any, TableOptions<FCIMovementItem> | undefined>({
            query: (params: TableOptions<FCIMovementItem>) => {
                const { page, pageSize, order, search, filters } = params || {};
                const adaptersFilter = filters?.map( f => createAdapterToFilterBackend(f,createAdapterToFieldFundsEndpoint));
                const serializedFilters = adaptersFilter ? JSON.stringify(adaptersFilter) : undefined;
                const serializedOrder = order ? JSON.stringify({field:createAdapterToFieldFundsEndpoint(order.field), order:order.order}) : undefined;
                
                return {
                    url: `${BASE_FUNDS_URL}/filtros`,
                    params: {
                        page: page ? page - 1 : 0,
                        pageSize,
                        order: serializedOrder,
                        search,
                        filters: serializedFilters
                    }
                }
            },
            providesTags: ["Funds"],
            transformResponse: (response: EndpointFundsList, _meta, params: TableOptions<FCIMovementItem>) => {
                const page = params?.page ?? 1;
                const endpointResponse = response?.rows?.map((fund: FCIMovementItemEndpoint) => 
                    createAdapterEndpointToFunds(fund)
                );
                return {
                    data: endpointResponse,
                    page,
                    total: response.rows
                };
           }
        }),
        createFund: builder.mutation<any, FCIMovementItem>({
            query: (data) => ({
                url: `${BASE_FUNDS_MUTATION_URL}`,
                method: "POST",
                body: {
                    Content: createAdapterToFundsEndpoint(data),
                },
            }),
            invalidatesTags: ["Funds"],
        }),
        createImportFunds: builder.mutation<any, any>({
            query: (movimientos) => ({
                url: `${BASE_FUNDS_MUTATION_URL}/import`,
                method: "POST",
                body: {
                    movimientos: movimientos.map((r: any) => createAdapterFromFCIMovementToEndpoint(r))
                }
            }),
            invalidatesTags: ["Funds"],
        }),
        revaluateFunds: builder.mutation<FCIMovementItem, FCIMovementItem | undefined>({
            query: () => ({
                url: `${BASE_FUNDS_MUTATION_URL}/revaluate`,
                method: "GET",
            }),
            invalidatesTags: ["Funds"],
        }),
        updateFund: builder.mutation<any, FCIMovementItem>({
            query: (data: FCIMovementItem) => ({
                url: `${BASE_FUNDS_MUTATION_URL}`,
                method: "PUT",
                body: {
                    Content: createAdapterToFundsEndpoint(data),
                },
            }),
            invalidatesTags: (_res,_err, data: FCIMovementItem) => ["Funds", {type: "Funds", id: data?.fciMovementId}],
        }),
        deleteFund: builder.mutation<void, number>({
            query: (id) => ({
                url: `${BASE_FUNDS_MUTATION_URL}/${id}`,
                method: "DELETE",
            }),
            invalidatesTags: ["Funds"],
        }),
    }),
});

export const {
    useGetFundQuery,
    useGetFundsQuery,
    useCreateFundMutation,
    useCreateImportFundsMutation,
    useRevaluateFundsMutation,
    useUpdateFundMutation,
    useDeleteFundMutation
} =  fundsApi;